import * as z from 'zod';
import { HttpEndpoint } from '../http/HttpEndpoint';
import { endpoint } from '../http/endpoint';
import { PatchPaymentCredentialsRuntype, PaymentCredentialsRuntype } from '../orders';
import { PreferenceStatusEnumRuntype } from '../part';
import { SiteRuntype } from './InventorySiteBackendTypes';
import { AssemblySettingsRuntype, PatchAssemblySettingsRuntype } from './assemblySettingsBackendTypes';
import {
    AutomaticRfqNumberSettingsRuntype,
    BomWarningsConfigurationRuntype,
    CalculationWithoutManufacturingSettingsRuntype,
    GlobalCurrencySettingsDTORuntype,
    ManufacturerPreferenceDTORuntype,
    ManufacturerPreferenceInputRuntype,
    ManufacturerPreferenceUpdateRuntype,
    PatchAutomaticRfqNumberSettingsRuntype,
    PatchSelfSignupSettingsRuntype,
    PublicExchangeRateSummaryDTORuntype,
    SelfSignupSettingsRuntype,
} from './organizationSettingsBackendTypes';
import {
    PatchSupplierPortalSettingsRuntype,
    SupplierPortalSettingsRuntype,
} from './supplierPortalSettingsBackendTypes';

const sitesDependentQueries: HttpEndpoint[] = [
    'GET /organization-settings/sites',
    'GET /resources',
    'GET /resources/:resourceId',
    'GET /expenses',
    'GET /expenses/:expenseId',
    'GET /activities',
    'GET /activities/:activityId',
];

export const organizationSettingsEndpoints = {
    'GET /organization-settings/organization-currency-settings': endpoint({
        description: 'Returns the current selected currency as well as other currency related information',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: GlobalCurrencySettingsDTORuntype }),
    }),

    'GET /organization-settings/assets': endpoint({
        description: "Returns the organization's assets",
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            navbar_dark: z.string(),
            navbar_light: z.string(),
            favicon: z.string(),
        }),
    }),
    'PATCH /organization-settings/assets': endpoint({
        description: "Updates the organization's assets",
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            navbar_dark: z.string(),
            navbar_light: z.string(),
            favicon: z.string(),
        }),
        responseBody: z.object({
            navbar_dark: z.string(),
            navbar_light: z.string(),
            favicon: z.string(),
        }),
        invalidates: ['GET /organization-settings/assets'],
    }),

    'PATCH /organization-settings/organization-currency-settings': endpoint({
        description: 'Updates current selected currency as well as manual exchange rates.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: GlobalCurrencySettingsDTORuntype,
        responseBody: z.object({ data: GlobalCurrencySettingsDTORuntype }),
    }),

    'GET /public-exchange-rate': endpoint({
        description: 'Returns the public exchange rate for the given currency',
        pathParams: z.undefined(),
        queryParams: z.object({ currency: z.string() }),
        requestBody: z.undefined(),
        responseBody: z.object({ data: PublicExchangeRateSummaryDTORuntype }),
    }),

    'GET /organization-settings/bom-warnings': endpoint({
        description: 'Fetch bom warning settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: BomWarningsConfigurationRuntype,
    }),

    'PATCH /organization-settings/bom-warnings': endpoint({
        description: 'Updates the bom warning settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: BomWarningsConfigurationRuntype,
        responseBody: BomWarningsConfigurationRuntype,
        invalidates: ['GET /organization-settings/bom-warnings'],
    }),

    'GET /organization-settings/webhooks/dashboard-url': endpoint({
        description: 'Get a url to the svix app portal.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.string(),
        requestBody: z.undefined(),
    }),

    'GET /organization-settings/payments/credentials': endpoint({
        description: 'Get the payment credentials for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        responseBody: PaymentCredentialsRuntype,
        requestBody: z.undefined(),
    }),

    'PATCH /organization-settings/payments/credentials': endpoint({
        description: 'Update the payment credentials for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchPaymentCredentialsRuntype,
        responseBody: PaymentCredentialsRuntype,
    }),

    'GET /organization-settings/sites': endpoint({
        description: 'Returns the sites of the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.object({
                sites: z.array(SiteRuntype),
            }),
        }),
    }),
    'POST /organization-settings/sites': endpoint({
        description: 'Updates the sites of an organization.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: SiteRuntype.omit({ id: true }),
        responseBody: z.object({ data: SiteRuntype }),
        invalidates: sitesDependentQueries,
    }),
    'PATCH /organization-settings/sites/:siteId': endpoint({
        description: 'Updates the sites of an organization.',
        pathParams: z.object({ siteId: z.string() }),
        queryParams: z.undefined(),
        requestBody: SiteRuntype.omit({ id: true }),
        responseBody: z.object({ data: SiteRuntype }),
        invalidates: sitesDependentQueries,
    }),
    'DELETE /organization-settings/sites/:siteId': endpoint({
        description: 'Delete the site of an organization.',
        pathParams: z.object({ siteId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ deleted: z.number() }),
        invalidates: sitesDependentQueries,
    }),
    'GET /organization-settings/self-signup': endpoint({
        description: 'Get the self-signup settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: SelfSignupSettingsRuntype,
    }),
    'PATCH /organization-settings/self-signup': endpoint({
        description: 'Update the self-signup settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchSelfSignupSettingsRuntype,
        responseBody: SelfSignupSettingsRuntype,
        invalidates: ['GET /organization-settings/self-signup'],
    }),

    'GET /organization-settings/calculation-without-manufacturing': endpoint({
        description: 'Get the calculation-without-manufacturing settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: CalculationWithoutManufacturingSettingsRuntype,
    }),
    'PATCH /organization-settings/calculation-without-manufacturing': endpoint({
        description: 'Update the calculation-without-manufacturing settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: CalculationWithoutManufacturingSettingsRuntype,
        responseBody: z.undefined(),
    }),

    // Manufacturer Preferences
    'GET /organization-settings/manufacturer-preferences': endpoint({
        description: 'Get the manufacturer preferences for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            items: z.array(ManufacturerPreferenceDTORuntype),
        }),
    }),
    'POST /organization-settings/manufacturer-preferences': endpoint({
        description: 'Add a manufacturer preference for a part category',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: ManufacturerPreferenceInputRuntype,
        responseBody: ManufacturerPreferenceDTORuntype,
        invalidates: ['GET /organization-settings/manufacturer-preferences'],
    }),

    'PATCH /organization-settings/manufacturer-preferences/:id/:status': endpoint({
        description: 'Updates the preferred manufacturer preference',
        pathParams: z.object({ id: z.number(), status: PreferenceStatusEnumRuntype }),
        queryParams: z.undefined(),
        requestBody: ManufacturerPreferenceUpdateRuntype,
        responseBody: ManufacturerPreferenceDTORuntype,
        invalidates: ['GET /organization-settings/manufacturer-preferences'],
    }),

    'DELETE /organization-settings/manufacturer-preferences/:partCategoryId/:status': endpoint({
        description: 'Delete the manufacturer preferences for a part category',
        pathParams: z.object({ partCategoryId: z.number(), status: PreferenceStatusEnumRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: ['GET /organization-settings/manufacturer-preferences'],
    }),

    // Assembly Settings
    'GET /organization-settings/assembly-settings': endpoint({
        description: 'Get the assembly settings for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: AssemblySettingsRuntype,
    }),

    'PATCH /organization-settings/assembly-settings': endpoint({
        description: 'Update the assembly settings for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchAssemblySettingsRuntype,
        responseBody: AssemblySettingsRuntype,
        invalidates: ['GET /organization-settings/assembly-settings'],
    }),

    'GET /organization-settings/auto-rfq-number': endpoint({
        description: 'Get automatic RfQ number settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: AutomaticRfqNumberSettingsRuntype,
    }),
    'PATCH /organization-settings/auto-rfq-number': endpoint({
        description: 'Update automatic RfQ number settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchAutomaticRfqNumberSettingsRuntype,
        responseBody: AutomaticRfqNumberSettingsRuntype,
        invalidates: ['GET /organization-settings/auto-rfq-number', 'GET /organization-settings/auto-rfq-number/next'],
    }),
    'GET /organization-settings/auto-rfq-number/next': endpoint({
        description: 'Get the auto-generated RfQ number for the next RfQ, if exists',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string().nullable(),
    }),
    'GET /organization-settings/supplier-portal-settings': endpoint({
        description: 'Get the supplier portal settings for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: SupplierPortalSettingsRuntype,
    }),
    'PATCH /organization-settings/supplier-portal-settings': endpoint({
        description: 'Update the supplier portal settings for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchSupplierPortalSettingsRuntype,
        responseBody: SupplierPortalSettingsRuntype,
        invalidates: ['GET /organization-settings/supplier-portal-settings'],
    }),

    'GET /organization-settings/checkout-conditions': endpoint({
        description: 'Get the checkout conditions for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(
            z.object({
                translation_en: z.string(),
                translation_de: z.string(),
                translation_fr: z.string(),
                translation_it: z.string(),
                translation_es: z.string(),
                rank: z.number(),
                created_at: z.string(),
            }),
        ),
    }),

    'POST /organization-settings/checkout-conditions/replace-all': endpoint({
        description: 'Deletes all previous checkout conditions and creates new ones',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.array(
            z.object({
                translation_en: z.string().nullable(),
                translation_de: z.string().nullable(),
                translation_fr: z.string().nullable(),
                translation_it: z.string().nullable(),
                translation_es: z.string().nullable(),
                rank: z.number().nullable(),
            }),
        ),
        responseBody: z.array(
            z.object({
                translation_en: z.string().nullable(),
                translation_de: z.string().nullable(),
                translation_fr: z.string().nullable(),
                translation_it: z.string().nullable(),
                translation_es: z.string().nullable(),
                rank: z.number().nullable(),
                created_at: z.string(),
            }),
        ),
        invalidates: ['GET /organization-settings/checkout-conditions'],
    }),

    'GET /organization-settings/feature-flags': endpoint({
        description: 'Get the feature flags for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            is_advanced_role_management_enabled: z.boolean(),
        }),
    }),
};
